import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { StatusBadge } from "@/components/StatusBadge";
import { OneSignalInit, enablePush } from "@/components/OneSignalInit";
import { DELIVERY_FLOW, STATUS_LABELS, ETA_PRESETS } from "@/lib/statuses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogOut, MapPin, Phone, Instagram, Truck, CheckCircle2, ShieldQuestion, Bell } from "lucide-react";
import { toast } from "sonner";

const token = () => localStorage.getItem("zr_token");
// Driver-visible progression (no customer_verified — that comes from code entry; no delivered until verified)
const DRIVER_STEPS = ["driver_assigned", "driver_picked_up", "on_the_way", "arriving_soon"];
const NEXT_LABEL = {
  driver_assigned: "Picked Up Order",
  driver_picked_up: "On The Way",
  on_the_way: "Arriving Soon",
};

export default function DriverDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [orders, setOrders] = useState([]);
  const [completed, setCompleted] = useState([]);

  const load = useCallback(async () => {
    try {
      const [a, c] = await Promise.all([api.get("/driver/orders"), api.get("/driver/completed")]);
      setOrders(a.data); setCompleted(c.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => { load(); }, 10000);
    return () => clearInterval(id);
  }, [load]);

  const list = tab === "active" ? orders : completed;

  return (
    <div className="min-h-screen zr-grid-bg">
      <OneSignalInit />
      <header className="sticky top-0 z-20 flex items-center justify-between px-5 py-4 bg-[#0A0B0E]/90 backdrop-blur-xl border-b border-zinc-900">
        <div className="flex items-center gap-3"><Brand /><span className="rounded-full border border-[#CCFF00]/40 px-2 py-0.5 font-heading text-[11px] font-bold uppercase text-[#CCFF00]">Runner</span></div>
        <div className="flex items-center gap-2">
          <button data-testid="driver-enable-alerts-btn" onClick={() => { enablePush({ role: "driver" }); toast.success("Allow notifications to receive job alerts"); }} className="flex h-9 items-center gap-1.5 rounded-full border border-[#CCFF00]/40 px-3 text-xs font-semibold text-[#CCFF00] hover:bg-[#CCFF00]/10"><Bell className="h-3.5 w-3.5" /> Alerts</button>
          <span className="hidden sm:block text-xs text-zinc-500">{user?.name}</span>
          <button data-testid="driver-logout-btn" onClick={() => { logout(); navigate("/login"); }} className="flex h-9 items-center gap-1.5 rounded-full border border-zinc-700 px-3 text-xs font-semibold text-zinc-300 hover:text-white"><LogOut className="h-3.5 w-3.5" /></button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Stat label="Active Jobs" value={orders.length} icon={Truck} />
          <Stat label="Completed" value={completed.length} icon={CheckCircle2} />
        </div>
        <div className="flex gap-2 mb-4">
          {[["active", "Active Jobs"], ["completed", "Completed"]].map(([k, l]) => (
            <button key={k} data-testid={`driver-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 font-heading text-sm font-bold uppercase ${tab === k ? "bg-[#CCFF00] text-black" : "border border-zinc-700 text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {list.length === 0 ? <p className="py-12 text-center text-zinc-500">No orders here.</p> : list.map((o) => <DriverCard key={o.id} o={o} reload={load} completed={tab === "completed"} />)}
      </div>
    </div>
  );
}

function DriverCard({ o, reload, completed }) {
  const [code, setCode] = useState("");
  const [ceta, setCeta] = useState({ minutes: "", window_start: "", window_end: "", message: "" });
  const addr = o.delivery_address;
  const mapsUrl = addr ? `https://maps.google.com/?q=${encodeURIComponent(`${addr.street} ${addr.apt} ${addr.city} ${addr.state} ${addr.zip}`)}` : null;
  const firstName = (o.full_name || "").split(" ")[0];

  const act = async (fn, msg) => { try { await fn(); toast.success(msg || "Updated"); reload(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const setStatus = (status) => act(() => api.patch(`/driver/orders/${o.id}/status`, { status }), "Status updated");
  const setEta = (p) => act(() => api.patch(`/driver/orders/${o.id}/eta`, { label: p.label, minutes: p.minutes }), "ETA sent");
  const sendCustomEta = () => {
    const mins = ceta.minutes === "" ? null : parseInt(ceta.minutes, 10);
    const label = mins != null && !Number.isNaN(mins) ? `${mins} minutes` : "Custom ETA";
    act(() => api.patch(`/driver/orders/${o.id}/eta`, { label, minutes: mins, window_start: ceta.window_start, window_end: ceta.window_end, message: ceta.message }), "ETA sent");
  };
  const verify = async () => {
    try { const { data } = await api.post(`/driver/orders/${o.id}/verify-code`, { code }); if (data.matched) { toast.success(data.message); reload(); } else toast.error(data.message); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stepIdx = DRIVER_STEPS.indexOf(o.status);
  const nextStatus = stepIdx >= 0 && stepIdx < DRIVER_STEPS.length - 1 ? DRIVER_STEPS[stepIdx + 1] : null;
  const canAdvance = nextStatus && NEXT_LABEL[o.status];

  return (
    <div data-testid={`driver-order-${o.order_number}`} className="mb-3 rounded-xl border border-zinc-800 bg-zinc-900/70 p-4">
      <div className="flex items-center justify-between"><span className="font-mono text-[#CCFF00]">{o.order_number}</span><StatusBadge status={o.status} /></div>
      <div className="mt-2 space-y-1 text-sm text-zinc-300">{o.items?.map((i, x) => <div key={x}>{i.qty}× {i.name}{i.flavor ? ` · ${i.flavor}` : ""}{i.extras && i.extras.length ? ` · + ${i.extras.join(", ")}` : ""}</div>)}</div>

      <div className="mt-2 flex items-center gap-3 rounded-lg border border-zinc-800 bg-black/30 p-2">
        {o.recognition?.type === "photo" && o.recognition.photo_path ? (
          <img data-testid={`driver-rec-${o.order_number}`} src={`${API}/files/${o.recognition.photo_path}?auth=${token()}`} alt="recognition" className="h-14 w-14 rounded-md object-cover" />
        ) : o.recognition?.type === "instagram" ? (
          <a data-testid={`driver-ig-link-${o.order_number}`} href={`https://instagram.com/${o.recognition.instagram}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-sm text-pink-300 hover:underline"><Instagram className="h-4 w-4" /> @{o.recognition.instagram}</a>
        ) : <span className="text-xs text-zinc-500">No recognition</span>}
        <div className="text-xs text-zinc-300"><p className="font-semibold text-white">{firstName}</p><p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {o.phone}</p></div>
      </div>

      {addr && <p className="mt-2 flex items-center gap-1.5 text-xs text-zinc-400"><MapPin className="h-3.5 w-3.5 text-[#CCFF00]" /> {addr.street}{addr.apt ? `, ${addr.apt}` : ""}, {addr.city}, {addr.state} {addr.zip} {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="text-[#CCFF00] hover:underline">Maps</a>}</p>}
      {(o.instructions || addr?.instructions) && <p className="text-xs italic text-zinc-500">"{o.instructions || addr?.instructions}"</p>}

      {!completed && (
        <>
          {canAdvance && <Button data-testid={`advance-${o.order_number}-btn`} onClick={() => setStatus(nextStatus)} className="mt-3 w-full bg-violet-600 text-white font-bold hover:bg-violet-500">{NEXT_LABEL[o.status]}</Button>}

          <div className="mt-3">
            <p className="font-heading text-xs uppercase text-zinc-500 mb-1">Send ETA</p>
            <div className="flex flex-wrap gap-1.5">{ETA_PRESETS.map((p) => <button key={p.label} data-testid={`driver-eta-${o.order_number}-${p.minutes}`} onClick={() => setEta(p)} className="rounded-full border border-zinc-700 px-2.5 py-1 text-xs text-zinc-200 hover:border-[#CCFF00] hover:text-[#CCFF00]">{p.label}</button>)}</div>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              <Input data-testid={`driver-ceta-min-${o.order_number}`} type="number" min="0" value={ceta.minutes} onChange={(e) => setCeta({ ...ceta, minutes: e.target.value })} placeholder="Exact min" className="h-8 bg-zinc-900 border-zinc-700 text-white text-xs" />
              <Input data-testid={`driver-ceta-ws-${o.order_number}`} value={ceta.window_start} onChange={(e) => setCeta({ ...ceta, window_start: e.target.value })} placeholder="Window start" className="h-8 bg-zinc-900 border-zinc-700 text-white text-xs" />
              <Input data-testid={`driver-ceta-we-${o.order_number}`} value={ceta.window_end} onChange={(e) => setCeta({ ...ceta, window_end: e.target.value })} placeholder="Window end" className="h-8 bg-zinc-900 border-zinc-700 text-white text-xs" />
            </div>
            <div className="mt-1.5 flex gap-1.5">
              <Input data-testid={`driver-ceta-msg-${o.order_number}`} value={ceta.message} onChange={(e) => setCeta({ ...ceta, message: e.target.value })} placeholder="Message to customer (optional)" className="h-8 bg-zinc-900 border-zinc-700 text-white text-xs" />
              <Button data-testid={`driver-ceta-send-${o.order_number}`} onClick={sendCustomEta} className="h-8 shrink-0 bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33]">Set Custom</Button>
            </div>
          </div>

          <div className="mt-3 rounded-lg border border-[#CCFF00]/30 bg-[#CCFF00]/5 p-3">
            <p className="flex items-center gap-1.5 font-heading text-xs uppercase text-[#CCFF00]"><ShieldQuestion className="h-4 w-4" /> Ask customer for their 4-digit code</p>
            {o.verification_status === "verified" ? (
              <p className="mt-2 text-sm font-bold text-emerald-400">CUSTOMER VERIFIED — HAND OVER ORDER</p>
            ) : (
              <div className="mt-2 flex gap-2">
                <Input data-testid={`driver-code-${o.order_number}`} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter code" maxLength={4} className="h-9 bg-zinc-900 border-zinc-700 text-white font-mono text-center tracking-widest" />
                <Button data-testid={`driver-verify-${o.order_number}-btn`} onClick={verify} className="h-9 bg-[#CCFF00] text-black font-bold hover:bg-[#d4ff33] shrink-0">Verify</Button>
              </div>
            )}
          </div>

          {o.status === "customer_verified" && (
            <Button data-testid={`deliver-${o.order_number}-btn`} onClick={() => setStatus("delivered")} className="mt-3 w-full bg-emerald-600 text-white font-bold hover:bg-emerald-500">Mark Delivered</Button>
          )}
        </>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-4"><Icon className="h-5 w-5 text-[#CCFF00]" /><p className="mt-2 font-heading text-2xl font-black text-white">{value}</p><p className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</p></div>;
}
